import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Fade,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  TextField,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_CONFIG } from 'src/config/api.config';
import { useAuth } from 'src/contexts/AuthContext';

// Types for token payload
interface TokenPayload {
  sub: string; // email address
  companyId: string;
  role: string;
  exp: number;
  iat: number;
}

const MainContent = styled(Box)(
  ({ theme }) => `
    height: 100%;
    display: flex;
    flex: 1;
    overflow: auto;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: ${theme.colors.alpha.white[100]};
    position: relative;
    
    &:before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(135deg, rgba(0, 203, 117, 0.03) 0%, rgba(120, 120, 250, 0.03) 100%);
      z-index: 0;
    }
    
    @keyframes slideInFromLeft {
      0% {
        transform: translateX(-100%) scale(0.9);
        opacity: 0;
      }
      70% {
        transform: translateX(10px) scale(1.02);
      }
      85% {
        transform: translateX(-5px) scale(1);
      }
      100% {
        transform: translateX(0) scale(1);
        opacity: 1;
      }
    }
    
    @keyframes fadeOut {
      0% {
        opacity: 1;
        transform: scale(1);
      }
      100% {
        opacity: 0;
        transform: scale(0.95);
      }
    }
    
    @keyframes iconPulse {
      0%, 100% {
        transform: scale(1);
        opacity: 1;
      }
      50% {
        transform: scale(1.2);
        opacity: 0.9;
      }
    }
    
    @keyframes progressBar {
      0% {
        width: 100%;
      }
      100% {
        width: 0%;
      }
    }
`
);

const LoginContainer = styled(Container)(
  ({ theme }) => `
    padding: ${theme.spacing(4, 3)};
    position: relative;
    z-index: 1;
`
);

const LoginCard = styled(Paper)(
  ({ theme }) => `
    border-radius: 16px;
    padding: ${theme.spacing(4)};
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
    background: rgba(255, 255, 255, 0.98);
    backdrop-filter: blur(20px);
    overflow: hidden;
    position: relative;
    
    &:after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 6px;
      height: 100%;
      background: ${theme.colors.gradients.accso2};
    }
`
);

const LogoWrapper = styled(Box)(
  ({ theme }) => `
    margin-bottom: ${theme.spacing(3)};
    display: flex;
    justify-content: center;
    
    img {
      width: auto;
      height: 120px;
      max-width: 180px;
      object-fit: contain;
    }
`
);

const StyledButton = styled(Button)(
  ({ theme }) => `
    text-transform: uppercase;
    font-weight: bold;
    height: 46px;
    border-radius: 10px;
    background: ${theme.colors.gradients.accso2};
    box-shadow: ${theme.colors.shadows.primary};
    
    &:hover {
      background: ${theme.colors.gradients.accso3};
      box-shadow: ${theme.colors.shadows.accent};
    }
`
);

const StyledTextField = styled(TextField)(
  ({ theme }) => `
    & .MuiOutlinedInput-root {
      border-radius: 10px;
      background-color: ${theme.colors.alpha.white[100]};
      
      &.Mui-focused .MuiOutlinedInput-notchedOutline {
        border-color: ${theme.palette.primary.main};
        border-width: 2px;
      }
    }
    
    & .MuiInputLabel-root.Mui-focused {
      color: ${theme.palette.primary.main};
    }
`
);

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showRegistrationSuccess, setShowRegistrationSuccess] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false); // Add state for fade out animation
  // Add a ref to track component mount state
  const isMounted = useRef(true);
  const autoHideTimerRef = useRef<NodeJS.Timeout | null>(null); // Ref for auto-hide timer
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  // IMPORTANT: We use a global window variable here to solve a tricky edge case:
  // When login fails, the Login component completely unmounts and remounts due to
  // how the auth flow works. This causes any component state or refs to reset,
  // which would incorrectly show the registration success message again.
  //
  // Using window.__hasAttemptedLogin as a global flag ensures the notification
  // won't reappear after a failed login attempt even if the component fully remounts.
  useEffect(() => {
    if (
      (window as Window & { __hasAttemptedLogin?: boolean }).__hasAttemptedLogin
    ) {
      return; // Skip completely if login was attempted in this session
    }

    // Check for registration success in location state
    const hasRegistrationSuccess =
      location.state &&
      typeof location.state === 'object' &&
      'registrationSuccess' in location.state &&
      location.state.registrationSuccess === true;

    if (hasRegistrationSuccess) {
      // Show notification
      setShowRegistrationSuccess(true);

      // Clear location state immediately
      if (window.history.replaceState) {
        window.history.replaceState(null, document.title, location.pathname);
      }

      // More straightforward approach to auto-hiding with fade
      const DISPLAY_DURATION = 3000; // Show for 3 seconds
      const FADE_DURATION = 300; // Fade out over 300ms

      // First timeout - start fading after display duration
      setTimeout(() => {
        // Only proceed if still mounted
        if (
          document.body.contains(
            document.getElementById('registration-success-alert')
          )
        ) {
          setIsFadingOut(true);

          // Second timeout - remove after fade duration
          setTimeout(() => {
            setShowRegistrationSuccess(false);
            setIsFadingOut(false);
          }, FADE_DURATION);
        }
      }, DISPLAY_DURATION);
    }

    // Cleanup function
    return () => {
      isMounted.current = false;
      if (autoHideTimerRef.current) {
        clearTimeout(autoHideTimerRef.current);
        autoHideTimerRef.current = null;
      }
    };
  }, [location.pathname, location.state]); // Add location dependencies

  // Clear notification function - modified to support fade animation
  const clearNotification = () => {
    if (autoHideTimerRef.current) {
      clearTimeout(autoHideTimerRef.current);
      autoHideTimerRef.current = null;
    }

    // Start fade out animation
    setIsFadingOut(true);

    // Actually hide after animation completes
    setTimeout(() => {
      if (isMounted.current) {
        setShowRegistrationSuccess(false);
        setIsFadingOut(false); // Reset for next time
      }
    }, 300); // Match the Fade timeout

    // Set global login attempt flag
    (window as Window & { __hasAttemptedLogin?: boolean }).__hasAttemptedLogin =
      true;
  };

  const handleLogin = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    clearNotification();
    setError('');

    // Set global login attempt flag
    (window as Window & { __hasAttemptedLogin?: boolean }).__hasAttemptedLogin =
      true;

    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    setIsSubmitting(true);

    try {
      // Try with the auth context first (multiple attempts in case of transient failures)
      let success = false;

      try {
        success = await auth.login(email, password);
      } catch (authErr) {
        // Continue with fallback if auth.login throws
        console.error('Error during primary login attempt:', authErr);
      }

      // Only update state if component is still mounted
      if (!isMounted.current) {
        return;
      }

      if (success) {
        // If login was successful, redirect to dashboard
        redirectToDashboard();
      } else {
        // Only use direct API call as a fallback if auth.login fails

        let directCallSuccess = false;
        try {
          directCallSuccess = await testDirectApiCall(email, password);
        } catch (directErr) {
          console.error(
            'Login: Error from direct API call:',
            directErr instanceof Error ? directErr.message : String(directErr)
          );
        }

        // Check mounted state again after async operation
        if (!isMounted.current) {
          return;
        }

        if (directCallSuccess) {
          redirectToDashboard();
        } else {
          // Login failed - all attempts
          setError('Invalid credentials or server error. Please try again.');
        }
      }
    } catch (loginError) {
      // Only update state if component is still mounted
      if (!isMounted.current) {
        return;
      }

      console.error('Login: Error during login:', loginError);

      // Interface for API error structure
      interface ApiErrorResponse {
        response?: {
          data?: {
            message?: string;
          };
        };
        message?: string;
      }

      const apiError = loginError as ApiErrorResponse;

      // Display more specific error messages if available
      if (apiError?.response?.data?.message) {
        setError(apiError.response.data.message);
      } else if (apiError?.message) {
        setError(apiError.message);
      } else {
        setError('An error occurred during login. Please try again.');
      }

      // Optional: More specific error handling based on error type or message
      // Example: Check for specific error messages related to credentials
      if (
        loginError instanceof Error &&
        loginError.message.includes('credentials')
      ) {
        setError('Ungültige Anmeldeinformationen.');
      }

      // Example: Check for specific error messages related to email format
      if (
        loginError instanceof Error &&
        loginError.message.includes('email format')
      ) {
        const emailFormatError = loginError.message; // Renamed to avoid shadowing
        setError(emailFormatError);
      }
    } finally {
      // Only update state if component is still mounted
      if (isMounted.current) {
        setIsSubmitting(false);
      }
    }
  };

  // Check auth status on mount
  useEffect(() => {
    const checkAuthAndRedirect = () => {
      const token = localStorage.getItem('token');
      const userInfo = localStorage.getItem('user');

      if (token && userInfo && auth.isAuthenticated()) {
        navigate('/dashboards/main', { replace: true });
      }
    };

    // Only check if the auth loading is complete
    if (!auth.loading) {
      checkAuthAndRedirect();
    }
  }, [auth.loading, navigate, auth]);

  // Manual redirect function - use when we have a token but auth state might not be updated
  const redirectToDashboard = () => {
    navigate('/dashboards/main', { replace: true });
  };

  // Direct test function to bypass all the layers and test direct axios call
  const testDirectApiCall = async (userEmail: string, userPassword: string) => {
    try {
      // Use the full URL from API_CONFIG
      const loginUrl = API_CONFIG.getFullUrl(API_CONFIG.endpoints.login);

      const response = await axios.post(
        loginUrl,
        { email: userEmail, password: userPassword },
        { headers: { 'Content-Type': 'application/json' } }
      );

      // If we get a token back, we can store it directly
      if (response.data && response.data.token) {
        localStorage.setItem('token', response.data.token);

        try {
          // Create user info directly from token
          const token = response.data.token;
          const decodedToken = jwtDecode<TokenPayload>(token);

          if (decodedToken && decodedToken.sub) {
            const userInfo = {
              username: decodedToken.sub,
              displayName: extractDisplayName(decodedToken.sub),
              role: decodedToken.role,
              companyId: decodedToken.companyId
            };

            localStorage.setItem('user', JSON.stringify(userInfo));
          }
        } catch (err) {
          console.error('Error decoding token from direct call:', err);
        }
      }

      return true;
    } catch (apiError) {
      console.error('Direct API call failed:', apiError);
      return false;
    }
  };

  // Helper function to extract display name from email
  function extractDisplayName(emailAddress: string): string {
    // Extract the part before the @ symbol and capitalize first letters
    const nameParts = emailAddress.split('@')[0].split('.');
    return nameParts
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  return (
    <>
      <Helmet>
        <title>{t('login.title', 'Login - AccsoRail Management System')}</title>
      </Helmet>
      <MainContent>
        {showRegistrationSuccess && (
          <Fade in={!isFadingOut} timeout={300} unmountOnExit>
            <Alert
              id="registration-success-alert"
              icon={
                <CheckCircleIcon
                  fontSize="inherit"
                  sx={{ animation: 'iconPulse 1.5s infinite ease-in-out' }}
                />
              }
              severity="success"
              sx={{
                borderRadius: '12px',
                backgroundColor: 'rgba(46, 125, 50, 0.95)',
                color: 'white',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                boxShadow: '0 8px 20px rgba(0, 0, 0, 0.15)',
                backdropFilter: 'blur(8px)',
                '& .MuiAlert-icon': {
                  color: 'white',
                  marginRight: '16px',
                  fontSize: '22px'
                },
                width: 'auto',
                maxWidth: '450px',
                padding: '16px 20px',
                position: 'fixed',
                top: '40px',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 2000,
                transition: 'opacity 300ms ease-in-out'
              }}
            >
              <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ mb: 0.5, fontSize: '16px', color: 'white' }}
                >
                  {t(
                    'login.registration_success',
                    'Registration Submitted Successfully!'
                  )}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '14px',
                    opacity: 0.9,
                    lineHeight: 1.4,
                    color: 'white'
                  }}
                >
                  {t(
                    'login.registration_wait',
                    'Your registration request has been received. Please wait for administrator approval.'
                  )}
                </Typography>
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: -6,
                    left: -20,
                    height: '2px',
                    backgroundColor: 'rgba(255, 255, 255, 0.5)',
                    animation: 'progressBar 3s linear forwards'
                  }}
                  width="calc(100% + 40px)"
                />
              </Box>
            </Alert>
          </Fade>
        )}
        <LoginContainer maxWidth="sm">
          <LoginCard elevation={10}>
            <LogoWrapper>
              <img
                src="/static/images/logo/logoLogin.png"
                alt="Accso Logistics"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = '/static/images/logo/logo.svg';
                }}
              />
            </LogoWrapper>

            <Typography
              variant="subtitle1"
              align="center"
              sx={{
                mb: 4,
                color: 'text.secondary'
              }}
            >
              {t(
                'login.sign_in_to_continue',
                'Sign in to your account to continue'
              )}
            </Typography>

            {error && (
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  borderRadius: '10px',
                  '& .MuiAlert-icon': { color: '#d32f2f' }
                }}
              >
                {error}
              </Alert>
            )}

            <form onSubmit={handleLogin}>
              <Box mb={3}>
                <StyledTextField
                  fullWidth
                  label={t('login.email', 'Email')}
                  variant="outlined"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isSubmitting}
                  placeholder={t(
                    'login.email_placeholder',
                    'example@rail.accso.com'
                  )}
                  autoComplete="username"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonOutlineIcon sx={{ color: 'primary.main' }} />
                      </InputAdornment>
                    )
                  }}
                />
              </Box>
              <Box mb={3}>
                <StyledTextField
                  fullWidth
                  label={t('login.password', 'Password')}
                  type={showPassword ? 'text' : 'password'}
                  variant="outlined"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isSubmitting}
                  autoComplete="current-password"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlinedIcon sx={{ color: 'primary.main' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? (
                            <VisibilityOffIcon />
                          ) : (
                            <VisibilityIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Box>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={3}
              >
                <Link
                  href="/forgot-password"
                  variant="body2"
                  sx={{
                    color: 'primary.main',
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  {t('login.forgot_password', 'Forgot password?')}
                </Link>
                <Link
                  href="/register/rtu"
                  variant="body2"
                  sx={{
                    color: 'primary.main',
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  {t('login.register', 'Register')}
                </Link>
              </Box>
              <StyledButton
                fullWidth
                variant="contained"
                color="primary"
                type="submit"
                disabled={isSubmitting}
                sx={{
                  py: 1.2
                }}
              >
                {isSubmitting ? (
                  <>
                    <CircularProgress
                      size={24}
                      sx={{ color: 'white', mr: 1 }}
                    />
                    {t('login.signing_in', 'Signing in...')}
                  </>
                ) : (
                  t('login.sign_in', 'Sign in')
                )}
              </StyledButton>
            </form>

            <Box mt={4} textAlign="center">
              <Typography variant="caption" color="text.secondary">
                {t('footer.copyright', { year: currentYear })}
              </Typography>
            </Box>

            {/* Add Test Credentials Info
            <Alert severity="info" sx={{ mt: 3 }}>
              <Typography variant="body2">
                <strong>
                  {t('login.test_credentials', 'Test Credentials:')}
                </strong>
              </Typography>
              <Typography variant="caption" display="block" gutterBottom>
                {t(
                  'login.test_admin',
                  'Admin (JadeWeserPort): admin@jade-weser-port.de / changeme'
                )}
              </Typography>
              <Typography variant="caption" display="block" gutterBottom>
                {t(
                  'login.test_dispatcher',
                  'Dispatcher: dispatcher@jade-weser-port.de / changeme'
                )}
              </Typography>
              <Typography variant="caption" display="block">
                {t('login.test_rtu', 'RTU: rtu@jade-weser-port.de / changeme')}
              </Typography>
              <Typography variant="caption" display="block">
                {t(
                  'login.test_accounting',
                  'Accounting: accounting@jade-weser-port.de / changeme'
                )}
              </Typography>
            </Alert> */}
          </LoginCard>
        </LoginContainer>
      </MainContent>
    </>
  );
}

export default Login;
